import { api } from '@goaltree/backend/convex/_generated/api'
import { PREVIEW_W } from '@goaltree/backend/convex/lib/shareCards'
import { Button } from '@goaltree/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { Loader2, Share2 } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StreakFlame } from '@/components/streak-icons'
import { emitPositiveMoment } from '@/lib/positive-moment'
import { type MintedCard, cardImageUrl, openShareCard } from '@/lib/share-card'

export type StreakMoment = { habitId: string; n: number; title: string }

const BURST = 12
const FLAME = '#ff7a1a'

// Full-screen moment for a big streak milestone, with the story card ready to share.
export function StreakCelebration({
  moment,
  onClose,
}: {
  moment: StreakMoment | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const mintMany = useMutation(api.shareCards.mintMany)
  const [card, setCard] = useState<MintedCard | null>(null)
  const [loaded, setLoaded] = useState(false)
  const habitId = moment?.habitId

  useEffect(() => {
    setCard(null)
    setLoaded(false)
    if (!habitId) return
    let live = true
    mintMany({ targets: [{ type: 'streak', ref: habitId }] })
      .then(([c]) => {
        if (live && c) setCard(c as MintedCard)
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [habitId, mintMany])

  const close = () => {
    onClose()
    emitPositiveMoment()
  }
  const src = card && cardImageUrl(card, card.options.style, card.options, PREVIEW_W)

  return (
    <Dialog open={!!moment} onOpenChange={(o) => !o && close()}>
      {moment && (
        <DialogContent showCloseButton={false} className="max-w-xs text-center">
          <div className="relative mx-auto mt-1 flex items-center justify-center">
            {!reduce &&
              Array.from({ length: BURST }, (_, i) => {
                const a = (i / BURST) * Math.PI * 2
                return (
                  <motion.span
                    key={i}
                    aria-hidden
                    className="absolute h-2.5 w-1.5 rounded-full"
                    style={{ background: FLAME, rotate: `${(a * 180) / Math.PI + 90}deg` }}
                    initial={{ x: 0, y: 0, opacity: 0, scale: 0.4 }}
                    animate={{
                      x: Math.cos(a) * 120,
                      y: Math.sin(a) * 160,
                      opacity: [0, 1, 0],
                      scale: 1,
                    }}
                    transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
                  />
                )
              })}
            <motion.div
              initial={reduce ? false : { scale: 0.6, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 240, damping: 16 }}
              className="relative aspect-[9/16] h-[42dvh] max-h-[340px] overflow-hidden rounded-xl bg-muted shadow-lg ring-1 ring-foreground/10"
            >
              {src && (
                <img
                  src={src}
                  alt={t('shareCard.title.streak')}
                  onLoad={() => setLoaded(true)}
                  onError={() => setLoaded(true)}
                  className={cn(
                    'absolute inset-0 size-full object-cover transition-opacity duration-200',
                    loaded ? 'opacity-100' : 'opacity-0',
                  )}
                />
              )}
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              )}
            </motion.div>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-orange-500">
            <StreakFlame /> {t('shareCard.milestone.eyebrow')}
          </p>
          <DialogTitle className="text-xl font-extrabold tracking-tight">
            {t('shareCard.milestone.title', { count: moment.n })}
          </DialogTitle>
          <p className="truncate text-sm text-muted-foreground">{moment.title}</p>
          <div className="mt-1 flex flex-col gap-2">
            <Button
              className="w-full"
              onClick={() => {
                close()
                openShareCard({ type: 'streak', ref: moment.habitId, habit: moment.habitId })
              }}
            >
              <Share2 className="size-4" />
              {t('shareCard.share')}
            </Button>
            <Button variant="ghost" className="w-full" onClick={close}>
              {t('shareCard.milestone.notNow')}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}
